import { Button } from "@/components/ui/button"
import { classes } from "@/utils"
import { ChevronDown, ChevronUp, LoaderCircle, Wand2, X, CircleAlert, CircleCheck } from "lucide-react"
import { BulkCodingWizSheet, bulk_coding_wiz_row_label } from "./BulkCodingWizSheet"
import {
  BulkCodingWizResultsOpen,
  BulkCodingWizState,
  bulk_coding_wiz_counts,
  bulk_coding_wiz_is_finished,
  bulk_coding_wiz_row_failed,
  bulk_coding_wiz_summary,
  clear_bulk_coding_wiz,
  set_bulk_coding_wiz_minimized,
  type BulkCodingWizRowState,
  type BulkCodingWizRun,
} from "./bulk_coding_wiz_state"

/** Percentage of the run that has reached a terminal state, for the progress bar. */
export function bulk_coding_wiz_percent(done: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((done / total) * 100)
}

function open_results() {
  BulkCodingWizResultsOpen.set(true)
}

function row_summary(row: BulkCodingWizRowState): string {
  if (row.status === "error") return row.error
  if (row.status === "done") return row.outcome.summary_bullets.join("\n") || row.outcome.error || ""
  if (row.status === "running") return row.message ?? "Running…"
  return "Queued"
}

interface BulkCodingWizRowBadgeProps {
  deduction_id: string
  className?: string
}

/**
 * Per-row status for tables: a spinner with the latest progress line while CodingWiz works on
 * this transaction, then a green or red wand that opens the results. Renders nothing for rows
 * outside the current run.
 */
export function BulkCodingWizRowBadge({ deduction_id, className }: BulkCodingWizRowBadgeProps) {
  const row = BulkCodingWizState.use((run: BulkCodingWizRun | null) => run?.rows[deduction_id])
  if (!row) return null

  if (row.status === "queued") {
    return <span class={classes("text-xs text-gray-400 whitespace-nowrap", className)}>Queued</span>
  }
  if (row.status === "running") {
    return (
      <span class={classes("inline-flex items-center gap-1.5 text-xs text-sky-700 min-w-0", className)}>
        <LoaderCircle className="h-4 w-4 animate-spin shrink-0" />
        <span class="truncate max-w-[180px]" title={row.message ?? undefined}>
          {row.message ?? "Running…"}
        </span>
      </span>
    )
  }
  const failed = bulk_coding_wiz_row_failed(row)
  return (
    <button
      type="button"
      title={row_summary(row)}
      onClick={(event: MouseEvent) => {
        event.stopPropagation()
        open_results()
      }}
      class={classes(
        "inline-flex items-center shrink-0 cursor-pointer",
        failed ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800",
        className
      )}>
      <Wand2 className="h-4 w-4" />
    </button>
  )
}

function ActiveRows({ run }: { run: BulkCodingWizRun }) {
  const running = run.deduction_ids.filter((id: string) => run.rows[id]?.status === "running")
  if (running.length === 0) return null
  return (
    <ul class="flex flex-col gap-1 max-h-32 overflow-y-auto">
      {running.map((id: string) => {
        const row = run.rows[id]
        const message = row?.status === "running" ? row.message : null
        return (
          <li key={id} class="flex items-center gap-2 text-xs text-gray-600 min-w-0">
            <LoaderCircle className="h-3 w-3 animate-spin text-sky-600 shrink-0" />
            <span class="font-medium shrink-0">{bulk_coding_wiz_row_label(run, id)}</span>
            <span class="truncate text-gray-500">{message ?? "Starting…"}</span>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Floating status for the bulk CodingWiz run: a count, a progress bar and the rows currently in
 * flight. Lives in the app container so it follows the user across pages, and hosts the results
 * sheet so the completion toast's "View results" works from anywhere.
 */
export function BulkCodingWizProgress() {
  const run = BulkCodingWizState.use()
  const counts = bulk_coding_wiz_counts(run)
  const finished = bulk_coding_wiz_is_finished(counts)
  const done = counts.succeeded + counts.failed
  const percent = bulk_coding_wiz_percent(done, counts.total)

  return (
    <>
      <BulkCodingWizSheet />
      {run && run.minimized ? (
        <button
          type="button"
          onClick={() => set_bulk_coding_wiz_minimized(false)}
          class="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-sm shadow-lg hover:bg-gray-50">
          {finished ? (
            counts.failed > 0 ? (
              <CircleAlert className="h-4 w-4 text-red-600" />
            ) : (
              <CircleCheck className="h-4 w-4 text-emerald-600" />
            )
          ) : (
            <LoaderCircle className="h-4 w-4 animate-spin text-violet-600" />
          )}
          <span class="font-medium">CodingWiz</span>
          <span class="text-gray-600">{bulk_coding_wiz_summary(counts)}</span>
          <ChevronUp className="h-4 w-4 text-gray-400" />
        </button>
      ) : null}
      {run && !run.minimized ? (
        <div class="fixed bottom-4 right-4 z-40 w-80 rounded-lg border bg-white shadow-lg">
          <div class="flex items-center gap-2 px-3 py-2 border-b">
            {finished ? (
              counts.failed > 0 ? (
                <CircleAlert className="h-4 w-4 text-red-600 shrink-0" />
              ) : (
                <CircleCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              )
            ) : (
              <LoaderCircle className="h-4 w-4 animate-spin text-violet-600 shrink-0" />
            )}
            <div class="flex flex-col min-w-0 flex-1">
              <span class="text-sm font-semibold leading-tight">CodingWiz</span>
              <span class="text-xs text-gray-600">{bulk_coding_wiz_summary(counts)}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              title="Minimize"
              onClick={() => set_bulk_coding_wiz_minimized(true)}>
              <ChevronDown className="h-4 w-4" />
            </Button>
            {finished ? (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Dismiss" onClick={clear_bulk_coding_wiz}>
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
          <div class="px-3 py-2 flex flex-col gap-2">
            <div class="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                class={classes(
                  "h-full rounded-full transition-[width] duration-300",
                  finished && counts.failed > 0 ? "bg-amber-500" : "bg-violet-600"
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
            <ActiveRows run={run} />
            {!finished && counts.queued > 0 ? (
              <span class="text-xs text-gray-500">
                {counts.queued} waiting · {counts.running} running
              </span>
            ) : null}
            <div class="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={open_results}>
                View results
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("bulk_coding_wiz_percent", () => {
    it("rounds to a whole percent and survives an empty run", () => {
      expect(bulk_coding_wiz_percent(0, 0)).toBe(0)
      expect(bulk_coding_wiz_percent(1, 3)).toBe(33)
      expect(bulk_coding_wiz_percent(3, 3)).toBe(100)
    })
  })
}
